import {
  Badge,
  Box,
  Flex,
  Heading,
  Link,
  Separator,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react"
import type { ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import { useOpportunityDetail } from "../../hooks/useSourcingQueries"
import { getFinancials } from "../../services/portfolioApi"
import SummaryStatCard from "./SummaryStatCard"

interface OpportunityTabProps {
  opportunityId: number
}


// SDLT for companies claiming property rental business relief (from 1 Apr 2025)
// Standard rates + 5% higher-rates surcharge applied marginally
// Relief from 17% flat rate: gov.uk/guidance/stamp-duty-land-tax-corporate-bodies
const SDLT_BANDS = [
  { from: 0, to: 125_000, rate: 0.05 },
  { from: 125_000, to: 250_000, rate: 0.07 },
  { from: 250_000, to: 925_000, rate: 0.10 },
  { from: 925_000, to: 1_500_000, rate: 0.15 },
  { from: 1_500_000, to: Infinity, rate: 0.17 },
]

const calculateStampDuty = (price: number): number => {
  let tax = 0
  for (const band of SDLT_BANDS) {
    if (price <= band.from) break
    const taxable = Math.min(price, band.to) - band.from
    tax += taxable * band.rate
  }
  return tax
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return "–"
  return `£${Math.round(value).toLocaleString("en-GB")}`
}

const formatPriceStr = (price: string | null | undefined): string => {
  if (!price) return "–"
  const num = parseFloat(price)
  return isNaN(num) ? price : `£${Math.round(num).toLocaleString("en-GB")}`
}

const getListingStatusBadge = (
  status: string
): { colorPalette: string; label: string } => {
  switch (status) {
    case "for_sale":
      return { colorPalette: "green", label: "For Sale" }
    case "under_offer":
      return { colorPalette: "orange", label: "Under Offer" }
    case "sold_stc":
      return { colorPalette: "red", label: "Sold STC" }
    case "sold":
      return { colorPalette: "red", label: "Sold" }
    default:
      return { colorPalette: "gray", label: status }
  }
}

interface DealRowProps {
  label: string
  children: ReactNode
  bold?: boolean
}

const DealRow = ({ label, children, bold = false }: DealRowProps) => (
  <Flex justify="space-between" align="center" py={2.5}>
    <Text fontSize="sm" color={bold ? "gray.900" : "gray.600"} fontWeight={bold ? "semibold" : "normal"}>
      {label}
    </Text>
    <Box textAlign="right">{children}</Box>
  </Flex>
)

interface SectionProps {
  title: string
  children: ReactNode
}

const Section = ({ title, children }: SectionProps) => (
  <Box>
    <Text fontSize="sm" fontWeight="bold" color="gray.900" mb={1}>
      {title}
    </Text>
    <Separator mb={1} />
    {children}
  </Box>
)

const SectionCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
    <Box px={5} py={4} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
      <Heading size="sm" color="gray.800">{title}</Heading>
    </Box>
    <Box px={5} py={2}>
      {children}
    </Box>
  </Box>
)

const SpecRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    py={3}
    borderBottom="1px solid"
    borderColor="gray.100"
    gap={3}
    _last={{ borderBottom: "none" }}
  >
    <Text fontSize="sm" color="gray.500" flexShrink={0} minW="120px">{label}</Text>
    <Box textAlign="right">{children}</Box>
  </Box>
)

const Placeholder = () => (
  <Text fontSize="sm" fontWeight="medium" color="gray.400">–</Text>
)

const Value = ({ children, color }: { children: ReactNode; color?: string }) => (
  <Text fontSize="sm" fontWeight="medium" color={color ?? "gray.900"}>{children}</Text>
)

const OpportunityTab = ({ opportunityId }: OpportunityTabProps) => {
  const { data: opportunity, isLoading } = useOpportunityDetail(opportunityId)
  const homeId = opportunity?.scrapedProperty.home.id ?? 0
  const { data: financials } = useQuery({
    queryKey: ["financials", homeId],
    queryFn: () => getFinancials(homeId),
    enabled: homeId > 0,
  })

  if (isLoading) {
    return <Text p={4}>Loading opportunity details...</Text>
  }

  if (!opportunity) {
    return <Text p={4}>Opportunity details not available.</Text>
  }

  const statusBadge = getListingStatusBadge(opportunity.listingStatus)
  const priceHistory = opportunity.scrapedProperty.priceHistory ?? []
  const home = opportunity.scrapedProperty.home

  const askingPriceNum = opportunity.scrapedProperty.price
    ? parseFloat(opportunity.scrapedProperty.price)
    : null
  const deposit = askingPriceNum != null ? askingPriceNum * 0.25 : null
  const stampDuty = askingPriceNum != null ? calculateStampDuty(askingPriceNum) : null
  const retrofitCostGross = home.upgradesTotalCost
  const heatPumpGrant = home.upgradesHeatPumpGrant ?? 0
  const retrofitCost = retrofitCostGross != null ? retrofitCostGross - heatPumpGrant : null
  const totalCashIn = askingPriceNum != null && retrofitCost != null
    ? askingPriceNum + (stampDuty ?? 0) + retrofitCost
    : null
  const annualSavings = home.upgradesTotalAnnualSavings
  const paybackPeriod = home.upgradesPaybackPeriod

  const rentEstimate = opportunity.scrapedProperty.rentEstimate
  const monthlyCashflow = rentEstimate
  const annualCashflow = rentEstimate != null ? rentEstimate * 12 : null
  // Gross yield on equity: gross annual rent / equity deployed (deposit + stamp duty + retrofit)
  const actualCashIn =
    deposit != null && stampDuty != null
      ? deposit + stampDuty + (retrofitCost ?? 0)
      : null
  const roi =
    annualCashflow != null && actualCashIn != null && actualCashIn > 0
      ? (annualCashflow / actualCashIn) * 100
      : null

  const lastCheckedDate = opportunity.lastCheckedAt ?? opportunity.createdAt
  const lastCheckedDisplay = new Date(lastCheckedDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  const daysOnMarket = opportunity.scrapedProperty.daysOnMarket
  const isMotivatedSeller = daysOnMarket != null && daysOnMarket >= 60

  return (
    <VStack gap={6} align="stretch" overflowX="hidden" maxW="100%" pb={20}>
      <Box bg="white" p={6} borderRadius="lg" overflowX="hidden" maxW="100%">
        <VStack gap={6} align="stretch">

          {/* Summary stat cards – full width */}
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
            <SummaryStatCard
              title="Total Cash In"
              value={formatCurrency(totalCashIn)}
              subline="acquisition + retrofit & refurbs"
              borderColor="gray.100"
            />
            <SummaryStatCard
              title="Gross Rent p.a."
              value={annualCashflow != null ? formatCurrency(annualCashflow) : "–"}
              borderColor="gray.100"
            />
            <SummaryStatCard
              title="Gross Yield on Equity"
              value={roi != null ? `${roi.toFixed(1)}%` : "–"}
              subline="annual rent / equity deployed"
              borderColor="gray.100"
            />
          </SimpleGrid>

          {/* 50/50 split */}
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} alignItems="start">

            {/* Left column: deal sections */}
            <VStack gap={6} align="stretch">

            {/* Cash In */}
            <Section title="Cash In">
              <DealRow label="Asking Price">
                <Value>{formatPriceStr(opportunity.scrapedProperty.price)}</Value>
              </DealRow>
              <DealRow label="Deposit (25%)">
                <Value>{formatCurrency(deposit)}</Value>
              </DealRow>
              <DealRow label="Stamp Duty & Legals">
                <Value>{formatCurrency(stampDuty)}</Value>
              </DealRow>
              <DealRow label="Retrofit Cost (with grant)">
                <Value>{formatCurrency(retrofitCost)}</Value>
              </DealRow>
              <DealRow label="Total Cash In" bold>
                <Value>{formatCurrency(totalCashIn)}</Value>
              </DealRow>
            </Section>

            {/* Returns */}
            <Section title="Returns">
              <DealRow label="Rent Estimate">
                {opportunity.scrapedProperty.rentEstimate != null
                  ? <Value>{formatCurrency(opportunity.scrapedProperty.rentEstimate)}/mo</Value>
                  : <Placeholder />}
              </DealRow>
              <DealRow label="Gross Yield">
                {opportunity.scrapedProperty.grossYield != null
                  ? <Value>{opportunity.scrapedProperty.grossYield.toFixed(2)}%</Value>
                  : <Placeholder />}
              </DealRow>
              <DealRow label="Annual Energy Savings">
                <Value>{formatCurrency(annualSavings)}</Value>
              </DealRow>
              <DealRow label="Gross Rent p.m.">
                {monthlyCashflow != null
                  ? <Value color={monthlyCashflow >= 0 ? "green.600" : "red.500"}>{formatCurrency(monthlyCashflow)}/mo</Value>
                  : <Placeholder />}
              </DealRow>
              <DealRow label="Gross Rent p.a." bold>
                {annualCashflow != null
                  ? <Value color={annualCashflow >= 0 ? "green.600" : "red.500"}>{formatCurrency(annualCashflow)}</Value>
                  : <Placeholder />}
              </DealRow>
            </Section>

            {/* Retrofit & Refurbs */}
            <Section title="Retrofit & Refurbs">
              <DealRow label="Total Retrofit Cost (with grant)">
                <Value>{formatCurrency(retrofitCost)}</Value>
              </DealRow>
              <DealRow label="Gas Boiler Decommissioning">
                {financials != null
                  ? <Value>{formatCurrency(financials.gasBoilerDecommissioningCost)}</Value>
                  : <Placeholder />}
              </DealRow>
              <DealRow label="Estimated Refurbs Cost">
                <Placeholder />
              </DealRow>
              <DealRow label="Annual Energy Savings">
                <Value>{formatCurrency(annualSavings)}</Value>
              </DealRow>
              <DealRow label="Payback Period">
                <Value>{paybackPeriod != null ? `${paybackPeriod} yrs` : "–"}</Value>
              </DealRow>
              <DealRow label="Retrofit ROI">
                <Value>{home.upgradesRoi != null ? `${home.upgradesRoi.toFixed(1)}%` : "–"}</Value>
              </DealRow>
            </Section>
            </VStack>

            {/* Right column: Price History + Listing Details */}
            <VStack gap={4} align="stretch">
              {priceHistory.length > 0 && (
                <SectionCard title="Price History">
                  {priceHistory.map((entry) => (
                    <SpecRow
                      key={entry.id}
                      label={new Date(entry.observedAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    >
                      <Text fontSize="sm" fontWeight="medium" color="gray.800">
                        {formatPriceStr(entry.price)}
                      </Text>
                    </SpecRow>
                  ))}
                </SectionCard>
              )}

              <SectionCard title="Listing Details">
                <SpecRow label="Status">
                  <Badge colorPalette={statusBadge.colorPalette} size="sm">
                    {statusBadge.label}
                  </Badge>
                </SpecRow>
                <SpecRow label="Last Checked">
                  <Text fontSize="sm" fontWeight="medium" color="gray.800">{lastCheckedDisplay}</Text>
                </SpecRow>
                {daysOnMarket != null && (
                  <SpecRow label="Days on Market">
                    <Text fontSize="sm" fontWeight="medium" color={isMotivatedSeller ? "orange.500" : "gray.800"}>
                      {daysOnMarket} days{isMotivatedSeller ? " (motivated seller?)" : ""}
                    </Text>
                  </SpecRow>
                )}
                {opportunity.scrapedProperty.rightmoveUrl && (
                  <SpecRow label="Listing">
                    <Link
                      href={opportunity.scrapedProperty.rightmoveUrl}
                      target="_blank"
                      fontSize="sm"
                      color="blue.500"
                    >
                      View on Rightmove ↗
                    </Link>
                  </SpecRow>
                )}
                {opportunity.scrapedProperty.title && (
                  <SpecRow label="Description">
                    <Text fontSize="sm" color="gray.700" maxW="60%" textAlign="right">
                      {opportunity.scrapedProperty.title}
                    </Text>
                  </SpecRow>
                )}
              </SectionCard>
            </VStack>

          </SimpleGrid>
        </VStack>
      </Box>
    </VStack>
  )
}

export default OpportunityTab
